import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Grid, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, IconButton, Alert, Snackbar, Tooltip,
} from '@mui/material'
import { CheckCircle, Cancel, ExpandMore, ExpandLess } from '@mui/icons-material'
import { kbApi } from '../services/api'

const getSeverityColor = (s: string) => {
  const map: any = { critical: 'error', high: 'warning', medium: 'info', low: 'success' }
  return map[s] || 'default'
}

const ProposedPatternsPanel = () => {
  const [proposed, setProposed] = useState<any[]>([])
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' })

  const fetchData = async () => {
    setLoading(true)
    try {
      const [proposedRes, statsRes] = await Promise.allSettled([
        kbApi.getProposedPatterns(),
        kbApi.getSelfLearningStats(),
      ])
      if (proposedRes.status === 'fulfilled') setProposed(proposedRes.value.data.proposed || [])
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data)
    } catch (e) {
      console.error('Failed to fetch proposed patterns:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [])

  const handleApprove = async (ruleId: string) => {
    try {
      await kbApi.approvePattern(ruleId)
      setProposed(prev => prev.filter(p => p.rule_id !== ruleId))
      setSnackbar({ open: true, message: 'Pattern approved and activated', severity: 'success' })
    } catch (e) {
      setSnackbar({ open: true, message: 'Approval failed', severity: 'error' })
    }
  }

  const handleReject = async (ruleId: string) => {
    try {
      await kbApi.rejectPattern(ruleId)
      setProposed(prev => prev.filter(p => p.rule_id !== ruleId))
      setSnackbar({ open: true, message: 'Pattern rejected', severity: 'success' })
    } catch (e) {
      setSnackbar({ open: true, message: 'Rejection failed', severity: 'error' })
    }
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>

  const proposalCounts = stats?.proposal_counts || {}

  return (
    <Box>
      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">{proposed.length}</Typography>
            <Typography variant="body2" color="text.secondary">Pending Review</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">{proposalCounts.approved || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Approved</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="error.main">{proposalCounts.rejected || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Rejected</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="info.main">
              {stats?.average_fix_success_rate != null ? `${(stats.average_fix_success_rate * 100).toFixed(0)}%` : '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">Avg Fix Rate</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Queue */}
      {proposed.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No proposed patterns awaiting review</Typography>
            <Typography variant="body2" color="text.secondary">
              New patterns will appear here when scanners discover them in the wild.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Domain</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Detections</TableCell>
                    <TableCell>Proposed</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proposed.map((p: any) => {
                    const isExpanded = expandedId === p.rule_id
                    return (
                      <>
                        <TableRow key={p.rule_id} hover>
                          <TableCell
                            onClick={() => setExpandedId(isExpanded ? null : p.rule_id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                              <Typography variant="subtitle2">{p.title}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={(p.domain || 'unknown').replace(/_/g, ' ')} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip label={p.severity} color={getSeverityColor(p.severity) as any} size="small" />
                          </TableCell>
                          <TableCell>{p.times_detected_in_wild || 0}</TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {p.proposed_at ? new Date(p.proposed_at).toLocaleDateString() : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Approve">
                              <IconButton size="small" color="success" onClick={() => handleApprove(p.rule_id)}>
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton size="small" color="error" onClick={() => handleReject(p.rule_id)}>
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow key={`${p.rule_id}-detail`}>
                            <TableCell colSpan={6} sx={{ bgcolor: 'grey.50', py: 2, px: 3 }}>
                              <Typography variant="subtitle2" gutterBottom>Description</Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>{p.description || 'No description'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Framework: {p.framework || 'general'} | Type: {p.type || 'unknown'} | Proposed by: {p.proposed_by || 'scanner'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Top Detected Rules */}
      {stats?.top_10_most_detected?.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Most Detected Patterns</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pattern</TableCell>
                    <TableCell>Domain</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell align="right">Detections</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.top_10_most_detected.map((r: any) => (
                    <TableRow key={r.rule_id}>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.domain}</TableCell>
                      <TableCell><Chip label={r.severity} color={getSeverityColor(r.severity) as any} size="small" /></TableCell>
                      <TableCell align="right">{r.times_detected_in_wild}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ProposedPatternsPanel
